import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  Calendar,
  DatePicker,
  TimePicker,
  Input,
  Select,
  Button,
  Typography,
  Row,
  Col,
  App,
} from "antd";
import dayjs from "dayjs";
import "./regularizeAttendanceModal.css";
import { fetchEmployeeRegularizeTime } from "../../attendanceOverviewThunk";

const { Text } = Typography;

// Matches REGULARIZABLE_ATTENDANCE_TYPES on the backend (hrpayroll.js) --
// short_leave/short_* variants and emergency_leave are deliberately left
// out, they have their own dedicated application/reclassify flows and the
// backend rejects them here too.
const ATTENDANCE_TYPE_OPTIONS = [
  { label: "Present", value: "present" },
  { label: "Absence", value: "absence" },
  { label: "Leave (Paid)", value: "leave_with_pay" },
  { label: "Leave (Unpaid)", value: "leave_without_pay" },
  { label: "Sick Leave", value: "sick_leave" },
  { label: "Casual Leave", value: "casual_leave" },
  { label: "Annual Leave", value: "annual_leave" },
  { label: "Flexi Leave", value: "flexi_leave" },
];

const RegularizeAttendanceModal = ({
  visible,
  onClose,
  onRegularize,
  isDisabled,
}) => {
  const [dateTime, setDateTime] = useState(dayjs());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [attendanceType, setAttendanceType] = useState("present");
  const [checkIn, setCheckIn] = useState(dayjs());
  const [checkOut, setCheckOut] = useState(dayjs());
  const [notes, setNotes] = useState("");
  const dispatch = useDispatch();
  const { modal } = App.useApp();

  const isPresent = attendanceType === "present";

  // 🕒 live date/time updater
  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🧹 reset fields when closed
  useEffect(() => {
    if (!visible) {
      setFromDate(null);
      setToDate(null);
      setAttendanceType("present");
      setCheckIn(dayjs());
      setCheckOut(dayjs());
      setNotes("");
    }
  }, [visible]);

  useEffect(() => {
    if (!fromDate || !toDate) return;

    // If both selected and same day
    if (fromDate.isSame(toDate, "day")) {
      dispatch(fetchEmployeeRegularizeTime({ date: fromDate }))
        .unwrap()
        .then((res) => {
          const data = res?.data;

          if (data) {
            if (data.check_in) {
              setCheckIn(dayjs(data.check_in, "HH:mm:ss"));
            }

            // check_out may be null
            if (data.check_out) {
              setCheckOut(dayjs(data.check_out, "HH:mm:ss"));
            }
          }
        })

        .catch((err) => modal.error({ title: err.message }));
    }
  }, [fromDate, toDate]);

  // 🚫 disable all future dates
  const disableUpcomingDates = (current) =>
    current && current > dayjs().endOf("day");

  return (
    <Modal open={visible} onCancel={onClose} footer={null} width={800} centered  className="attendance-modal">
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">
            Regularize your Attendance
          </Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Calendar */}
      <div className="attendanceOverview-calendar-container mark-attendance-calendar-container" style={{ marginTop: "2rem" }}>
        <Calendar fullscreen={false} disabledDate={disableUpcomingDates} />
      </div>

      {/* Date & Time Fields */}
      <Row gutter={[16, 16]} style={{ marginTop: "2rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            From Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            format="DD-MMM-YYYY"
            disabledDate={disableUpcomingDates}
            data-testid="regularizeFromDate"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            To Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={toDate}
            onChange={(val) => setToDate(val)}
            format="DD-MMM-YYYY"
            disabledDate={disableUpcomingDates}
            data-testid="regularizeToDate"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Attendance Type <span className="form-span">*</span>
          </label>
          <Select
            style={{ width: "100%" }}
            value={attendanceType}
            onChange={setAttendanceType}
            options={ATTENDANCE_TYPE_OPTIONS}
            data-testid="regularize_attendance_type"
          />
        </Col>

        {isPresent && (
          <>
            <Col xs={24} sm={12}>
              <label className="form-label">
                Check In <span className="form-span">*</span>
              </label>
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                value={checkIn}
                onChange={(val) => setCheckIn(val)}
                data-testid="regularize_start_time"
              />
            </Col>

            <Col xs={24} sm={12}>
              <label className="form-label">
                Check Out <span className="form-span">*</span>
              </label>
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                value={checkOut}
                onChange={(val) => setCheckOut(val)}
                data-testid="regularize_end_time"
              />
            </Col>
          </>
        )}

        <Col xs={24} sm={12}>
          <label className="form-label">
            Notes <span className="form-span">*</span>
          </label>
          <Input
            placeholder="Notes"
            data-testid="regularize_notes"
            value={notes}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 50) setNotes(val);
            }}
            onKeyDown={(e) => {
              if (
                notes &&
                notes.toString().length >= 50 &&
                !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                  e.key
                )
              ) {
                e.preventDefault();
              }
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {notes.length}/50 characters
          </Text>
        </Col>
      </Row>

      {/* Footer Buttons */}
      <div className="attendance-buttons">
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          data-testid="submit_regularize"
          disabled={
            isDisabled ||
            !fromDate ||
            !toDate ||
            !attendanceType ||
            (isPresent && (!checkIn || !checkOut)) ||
            !notes
          }
          onClick={() =>
            onRegularize({
              fromDate: fromDate?.format("YYYY-MM-DD"),
              toDate: toDate?.format("YYYY-MM-DD"),
              attendance: attendanceType,
              // Only "present" has real check-in/out times -- omitted
              // entirely for absence/leave types rather than sending
              // whatever stale time happens to be left in the picker.
              checkIn: isPresent ? checkIn?.format("HH:mm:ss") : null,
              checkOut: isPresent ? checkOut?.format("HH:mm:ss") : null,
              notes,
            })
          }
        >
          Mark Attendance
        </Button>
      </div>
    </Modal>
  );
};

export default RegularizeAttendanceModal;
